import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Upload, Image as ImageIcon, X } from 'lucide-react';
import { MediaFileSelector } from '@/components/media/MediaFileSelector';
import { useClient } from '../../contexts/ClientContext';
import mediaService from '../../services/mediaService';
import AuthenticatedImage from '@/components/media/AuthenticatedImage';

interface ImageToVideoFormProps {
  onSubmit: (data: ImageToVideoData) => void;
  isLoading?: boolean;
  formData: ImageToVideoData;
  setFormData: React.Dispatch<React.SetStateAction<ImageToVideoData>>;
}

export interface ImageToVideoData {
  input_media_file_id: string;
  prompt?: string;
  duration?: number;
  motion_type?: string;
  resolution?: string;
}

export const ImageToVideoForm: React.FC<ImageToVideoFormProps> = ({
  onSubmit,
  isLoading = false,
  formData,
  setFormData
}) => {
  const [selectedImage, setSelectedImage] = useState<any>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { currentClient } = useClient();

  const updateFormData = (field: keyof ImageToVideoData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageSelect = (mediaFile: any) => {
    setSelectedImage(mediaFile);
    updateFormData('input_media_file_id', mediaFile.id);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !currentClient) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      alert('File size must be less than 10MB');
      return;
    }

    try {
      setIsUploading(true);
      const response = await mediaService.uploadFile(file, currentClient.id);
      const uploadedFile = { id: response.file.id, ...response.file };
      setSelectedImage(uploadedFile);
      updateFormData('input_media_file_id', uploadedFile.id);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      alert(error.response?.data?.detail || 'Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Compact Source Image */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Source Image</Label>
        <div className="p-3 bg-white/50 dark:bg-slate-800/50 rounded-xl border border-slate-300/50 dark:border-slate-600/50">
          {selectedImage ? (
            <div className="relative group">
              <AuthenticatedImage
                src={selectedImage.thumbnail_url || selectedImage.file_url}
                alt="Selected image"
                className="w-full h-32 object-cover rounded-lg shadow-inner"
                clientId={currentClient.id}
              />
              <div className="absolute top-2 right-2">
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="h-6 w-6 rounded-full bg-red-500/80 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => {
                    setSelectedImage(null);
                    updateFormData('input_media_file_id', '');
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <MediaFileSelector
                onSelect={handleImageSelect}
                selectedFile={selectedImage}
                acceptedTypes={['image']}
                placeholderText="Select from Library"
                variant="clean"
                className="h-20"
              />
              <div 
                className="relative flex flex-col items-center justify-center h-20 bg-transparent border-dashed border-slate-300/80 dark:border-slate-600/80 rounded-lg cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-950/20 transition-colors duration-200"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={isUploading}
                />
                {isUploading ? (
                  <>
                    <Loader2 className="h-5 w-5 text-blue-500 animate-spin mb-1" />
                    <span className="text-xs text-slate-600 dark:text-slate-400">Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload className="h-5 w-5 text-blue-500 mb-1" />
                    <span className="text-xs text-center text-slate-600 dark:text-slate-400">Upload New</span>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Compact Video Settings */}
      <div className="grid grid-cols-3 gap-3">
        {/* Duration */}
        <div className="space-y-1">
          <Label className="text-xs font-medium text-slate-700 dark:text-slate-300">Duration</Label>
          <Select 
            value={formData.duration?.toString() || '5'} 
            onValueChange={(value) => updateFormData('duration', parseInt(value))}
          >
            <SelectTrigger className="h-9 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 hover:border-blue-400 dark:hover:border-blue-500 transition-colors text-sm">
              <SelectValue placeholder="Duration" />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600">
              <SelectItem value="3">3s</SelectItem>
              <SelectItem value="5">5s</SelectItem>
              <SelectItem value="10">10s</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Motion Type */}
        <div className="space-y-1">
          <Label className="text-xs font-medium text-slate-700 dark:text-slate-300">Motion</Label>
          <Select 
            value={formData.motion_type || 'subtle'} 
            onValueChange={(value) => updateFormData('motion_type', value)}
          >
            <SelectTrigger className="h-9 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 hover:border-purple-400 dark:hover:border-purple-500 transition-colors text-sm">
              <SelectValue placeholder="Motion" />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600">
              <SelectItem value="subtle">Subtle</SelectItem>
              <SelectItem value="moderate">Moderate</SelectItem>
              <SelectItem value="dynamic">Dynamic</SelectItem>
              <SelectItem value="cinematic">Cinematic</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Resolution */}
        <div className="space-y-1">
          <Label className="text-xs font-medium text-slate-700 dark:text-slate-300">Quality</Label>
          <Select 
            value={formData.resolution || '1024x576'} 
            onValueChange={(value) => updateFormData('resolution', value)}
          >
            <SelectTrigger className="h-9 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 hover:border-pink-400 dark:hover:border-pink-500 transition-colors text-sm">
              <SelectValue placeholder="Quality" />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600">
              <SelectItem value="512x512">512p</SelectItem>
              <SelectItem value="1024x576">HD</SelectItem>
              <SelectItem value="768x768">768p</SelectItem>
              <SelectItem value="1280x720">720p</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Compact Video Prompt */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center space-x-2">
          <span>Motion Description</span>
          <span className="text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-full">Optional</span>
        </Label>
        <Textarea
          placeholder="Describe the motion: 'gentle swaying', 'camera zoom', 'floating elements'..."
          value={formData.prompt || ''}
          onChange={(e) => updateFormData('prompt', e.target.value)}
          className="min-h-[60px] bg-white/50 dark:bg-slate-800/50 border-slate-300/50 dark:border-slate-600/50 focus:border-blue-400 dark:focus:border-blue-500 resize-none transition-colors text-sm"
          maxLength={500}
        />
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Be specific for better results
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {formData.prompt?.length || 0}/500
          </p>
        </div>
      </div>


    </div>
  );
};